const isDateOnly = (value: string) => !value.includes('T');

export const formatDateAsUtc = (
  value: string | number | Date,
  locale?: string | string[],
  options?: Intl.DateTimeFormatOptions,
): string => {
  const dateObj = value instanceof Date ? value : new Date(value);
  return dateObj.toLocaleDateString(locale, { ...options, timeZone: 'UTC' });
};

export const formatDateAsLocal = (
  value: string | number | Date,
  locale?: string | string[],
  options?: Intl.DateTimeFormatOptions,
): string => {
  const dateObj = value instanceof Date ? value : new Date(value);
  return dateObj.toLocaleDateString(locale, options);
};

/**
 * @deprecated Use `formatDateAsUtc` (for date-only / UTC-midnight values) or
 * `formatDateAsLocal` (for full timestamps) instead. This function silently
 * guesses the timezone based on input shape, which hides bugs.
 */
export const formatDate = (
  value: string | number | Date,
  locale?: string | string[],
  options?: Intl.DateTimeFormatOptions,
): string => {
  const dateObj = value instanceof Date ? value : new Date(value);
  const needsUtc =
    typeof value === 'number' ||
    (typeof value === 'string' && isDateOnly(value));

  return dateObj.toLocaleDateString(locale, {
    ...options,
    ...(needsUtc && { timeZone: 'UTC' }),
  });
};

export const convertDateForReports = (value: string) =>
  formatDate(value, 'en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

export const getFormattedDate = (dateString?: string) => {
  if (!dateString) return '';
  return formatDate(dateString, 'en-US');
};

export const isDateExpired = (date: string | undefined) => {
  if (!date) {
    return;
  }
  const expirationDate = new Date(date);

  if (isDateOnly(date)) {
    const nowUtc = Date.now();
    const startOfTodayUtc = nowUtc - (nowUtc % 86_400_000);
    return expirationDate.getTime() < startOfTodayUtc;
  }

  return expirationDate.getTime() < Date.now();
};

export const currentYear = new Date().getFullYear();

export const isValidYear = (year: number | null) =>
  Number.isInteger(year) && /^[0-9]{4}$/.test(year?.toString() ?? '');
